/**
 * LiveAssistCard – timeline of all states, actions, and entities so far.
 * Shows summary piles (missed / pending / completed), state markers, action list with status,
 * and entities. Auto-scrolls to keep live or last completed in view.
 */
import React, { useRef, useEffect, useMemo } from 'react';
import {
  TurnAnalysisTurn,
  AgentAssistStep,
  AgentAssistCardData,
  TurnAnalysisEntityRecord,
  StateActionOrderedItem,
  StateActionViolationItem,
  StateEntityFullItem,
} from '../types/api';
import {
  buildStateRuns,
  itemStatusToLiveStatus as mapOrderedItemToLiveStatus,
  normalizedScriptConstraints,
  orderedRowBelongsToSection,
  promoteFocusStepTimelineStatus,
  resolveLatestOrderedItemForSection,
  scriptConstraintChipMeta,
  type StateRun,
} from '../utils/agentAssistLiveTimeline';
import { serializeEntityValue } from '../utils/entityValue';
import '../styles/components/LiveAssistCard.css';
import EntityValueCell from './EntityValueCell';

export type LiveActionStatus =
  | 'completed'
  | 'completed_proactive'
  | 'completed_mandatory_out_of_order'
  | 'completed_inorder_out_of_order'
  | 'completed_failed'
  | 'live'
  | 'next'
  | 'pending'
  | 'skipped'
  | 'skipped_mandatory'
  | 'skipped_inorder'
  | 'missed_mandatory'
  | 'missed_inorder';

/** Normalized per-step violation for UI (icon + label + severity). */
export interface LiveAssistViolationItem {
  icon: string;
  violationType: string;
  message: string;
  severity?: string;
}

export interface LiveAssistTimelineAction {
  key: string;
  step: string;
  /** Human-readable title (step_title); falls back in UI to formatted step id. */
  displayTitle?: string;
  subflowName?: string | null;
  scriptConstraints?: string[];
  state?: string;
  status: LiveActionStatus;
  primaryEntityName?: string;
  remark?: string;
  /** Per-step violations (sensitivity, etc.); mandatory/order missed stay in status only. */
  violations?: LiveAssistViolationItem[];
}

export interface LiveAssistTimelineState {
  key: string;
  stateTitle: string;
  turnIndex: number;
}

/** One state section: state marker + its actions (one snapshot per state, from last turn in that state). */
export interface LiveAssistTimelineSection {
  state: LiveAssistTimelineState;
  actions: LiveAssistTimelineAction[];
}

export type LiveAssistEntityStatus =
  | 'completed'       /* green */
  | 'completed_ack'   /* lighter green */
  | 'to_confirm'      /* amber */
  | 'incomplete_other' /* darker amber */
  | 'to_repeat'       /* darker amber */
  | 'not_filled';

export interface LiveAssistEntityItem extends TurnAnalysisEntityRecord {
  status: LiveAssistEntityStatus;
  relatedActionKey?: string;
}

interface LiveAssistCardProps {
  allTurns: TurnAnalysisTurn[];
  currentTurnNumber: number;
}

const formatStepLabel = (step?: string) =>
  (step || '')
    .split('_')
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' ') || '—';

function subflowCornerLabelFromName(name: string | null | undefined): string | null {
  const raw = (name ?? '').trim();
  if (!raw) return null;
  return formatStepLabel(raw.replace(/_/g, ' '));
}

/** Normalize backend violations (object[] or string[]) to LiveAssistViolationItem[]. */
function normalizeStepViolations(
  raw: (StateActionViolationItem | string)[] | undefined
): LiveAssistViolationItem[] {
  if (!raw || !Array.isArray(raw)) return [];
  return raw.map((v): LiveAssistViolationItem => {
    if (typeof v === 'string') {
      const lower = v.toLowerCase();
      const icon = lower.includes('mandatory') || lower.includes('order') ? '⚠️' : '🚨';
      return { icon, violationType: 'violation', message: v, severity: 'warning' };
    }
    return {
      icon: v.icon ?? '⚠️',
      violationType: v.violation_type ?? 'violation',
      message: v.message ?? '',
      severity: v.severity,
    };
  });
}

/** True if this violation is mandatory/order-missed (counts in missed pile, not violations pile). */
function isMissedTypeViolation(v: LiveAssistViolationItem): boolean {
  const t = (v.violationType ?? '').toLowerCase();
  const m = (v.message ?? '').toLowerCase();
  return (
    t.includes('mandatory') ||
    t.includes('order_missed') ||
    t.includes('inorder') ||
    m.includes('mandatory') ||
    m.includes('order missed') ||
    m.includes('in order')
  );
}

/** Extract action name from messages like "Mandatory action skipped: ask_intent_change" or "skipped: ask_intent_change". */
function getViolationTargetActionName(v: LiveAssistViolationItem): string | null {
  const m = (v.message ?? '').trim();
  const colon = m.lastIndexOf(':');
  if (colon >= 0) {
    const after = m.slice(colon + 1).trim();
    if (after.length > 0) return after;
  }
  return null;
}

/** True if this violation should be shown on the given action. If it names a specific action, only show on that action. */
function violationAppliesToAction(v: LiveAssistViolationItem, stepName: string): boolean {
  const target = getViolationTargetActionName(v);
  if (!target) return true;
  return stepName.toLowerCase() === target.toLowerCase();
}

function buildTimeline(
  allTurns: TurnAnalysisTurn[],
  currentTurnNumber: number
): { sections: LiveAssistTimelineSection[]; actions: LiveAssistTimelineAction[] } {
  const sections: LiveAssistTimelineSection[] = [];
  const lastCard = allTurns.length > 0 ? allTurns[allTurns.length - 1].agent_card : null;
  // Violations are now per-step (state_actions_ordered[].violations). Keep card-level violations out of timeline logic.
  const violations: string[] = [];

  const lastTurnNumber = allTurns.length > 0 ? allTurns[allTurns.length - 1].turn_number : 0;
  const waitingForCurrentTurnData = lastTurnNumber < currentTurnNumber;
  // When waiting for fetch: don't treat last section as past (avoids painting pending→completed then correcting = flicker).

  const runs: StateRun[] = buildStateRuns(allTurns);

  const replayTurnForFocus =
    allTurns.find((x) => x.turn_number === currentTurnNumber) ??
    (allTurns.length > 0 ? allTurns[allTurns.length - 1] : null);
  const focusStepId = (replayTurnForFocus?.focus_step_id ?? '').trim();

  runs.forEach((run, stateIdx) => {
    const stateTitle = run.stateTitle;
    const turnIdx = run.lastTurnIndex;
    const t = allTurns[turnIdx];
    const card = t.agent_card;
    const sectionAnchorStateKey = t.strict_state?.state ?? null;
    const isLastSection = stateIdx === runs.length - 1;
    const isPastState =
      t.turn_number < currentTurnNumber &&
      !(waitingForCurrentTurnData && isLastSection);
    const state: LiveAssistTimelineState = {
      key: `state-${stateTitle}-${stateIdx}-${t.turn_number}`,
      stateTitle,
      turnIndex: t.turn_number,
    };
    const sectionActions: LiveAssistTimelineAction[] = [];
    const ordered = card?.state_actions_ordered;
    const replayIdx = allTurns.findIndex((x) => x.turn_number === currentTurnNumber);
    if (ordered && ordered.length > 0) {
      // Merge violations from all turns in this run so they don't disappear when a later turn's card has no violations.
      ordered.forEach((item, actionIdx) => {
        if (!orderedRowBelongsToSection(item, sectionAnchorStateKey)) return;
        const stepName = item.step_id ?? item.canonical_action_name ?? item.step_text ?? '';
        if (!stepName) return;
        const stepNorm = stepName.toLowerCase();
        const itemForStatus =
          replayIdx >= 0 && replayIdx > run.lastTurnIndex
            ? resolveLatestOrderedItemForSection(
                allTurns,
                run,
                stateTitle,
                stepNorm,
                item,
                currentTurnNumber
              )
            : item;
        const displayTitle =
          (itemForStatus.step_title && itemForStatus.step_title.trim()) ||
          formatStepLabel(stepName);
        const mapped = mapOrderedItemToLiveStatus(itemForStatus, violations, isPastState);
        const status: LiveActionStatus =
          (itemForStatus.status ?? '').toLowerCase() === 'skipped'
            ? 'completed'
            : promoteFocusStepTimelineStatus(mapped, itemForStatus, {
                focusStepId,
                isLastSection,
              });
        const remark = itemForStatus.badge ?? undefined;
        let mergedViolations = normalizeStepViolations(itemForStatus.violations);
        if (run.startTurnIndex < run.lastTurnIndex) {
          const seenKeys = new Set(mergedViolations.map((v) => `${v.violationType}:${v.message}`));
          for (let ti = run.startTurnIndex; ti <= run.lastTurnIndex; ti++) {
            if (ti === turnIdx) continue;
            const oc = allTurns[ti]?.agent_card?.state_actions_ordered;
            if (!oc) continue;
            const other = oc.find(
              (o) =>
                (o.step_id ?? o.canonical_action_name ?? o.step_text ?? '') === stepName &&
                orderedRowBelongsToSection(o, sectionAnchorStateKey)
            );
            if (!other?.violations) continue;
            const normalized = normalizeStepViolations(other.violations);
            for (const v of normalized) {
              const key = `${v.violationType}:${v.message}`;
              if (!seenKeys.has(key)) {
                seenKeys.add(key);
                mergedViolations = mergedViolations.concat(v);
              }
            }
          }
        }
        // Only show a violation on the action it refers to (e.g. "Mandatory action skipped: ask_intent_change" only on ask_intent_change).
        const filteredViolations = mergedViolations.filter((v) => violationAppliesToAction(v, stepName));
        const sf = (itemForStatus.subflow_name ?? '').trim();
        const scriptConstraints = normalizedScriptConstraints(itemForStatus.constraints ?? undefined);
        sectionActions.push({
          key: `state-${stateTitle}-action-${actionIdx}-${stepName}`,
          step: stepName,
          displayTitle,
          subflowName: sf || undefined,
          scriptConstraints: scriptConstraints.length > 0 ? scriptConstraints : undefined,
          primaryEntityName: itemForStatus.primary_entity_name ?? undefined,
          status,
          remark,
          violations: filteredViolations.length > 0 ? filteredViolations : undefined,
        });
      });
    } else if (isLastSection && card) {
      // Fallback for current state when no state_actions_ordered
      const completedSteps = card.completed_steps ?? [];
      const ongoingSteps = card.ongoing_steps ?? [];
      const upcomingSteps = card.upcoming_steps ?? [];
      const missedSteps = card.missed_steps ?? [];
      const ongoingSet = new Set((ongoingSteps ?? []).map((s) => (s.step ?? '').toLowerCase()));
      const missedSet = new Set((missedSteps ?? []).map((s) => (s.step ?? '').toLowerCase()));
      const seenSteps = new Set<string>();
      const addAction = (
        step: AgentAssistStep,
        status: LiveActionStatus,
        remark?: string
      ) => {
        const stepName = step.step ?? '';
        if (!stepName) return;
        const dedupeKey = `${stepName.toLowerCase()}-${status}`;
        if (seenSteps.has(dedupeKey)) return;
        seenSteps.add(dedupeKey);
        sectionActions.push({
          key: `fallback-${sectionActions.length}-${stepName}`,
          step: stepName,
          displayTitle: formatStepLabel(stepName),
          state: step.state,
          status,
          remark,
        });
      };
      completedSteps.forEach((s) => {
        const remark = undefined;
        if (remark === 'Mandatory out of order')
          addAction(s, 'completed_mandatory_out_of_order', remark);
        else if (remark === 'In order missed')
          addAction(s, 'completed_inorder_out_of_order', remark);
        else if (remark === 'Proactive')
          addAction(s, 'completed_proactive', remark);
        else addAction(s, 'completed');
      });
      ongoingSteps.forEach((s) => addAction({ step: s.step, state: s.step }, 'live'));
      upcomingSteps.forEach((s) => {
        const stepName = (s.step ?? '').toLowerCase();
        if (!ongoingSet.has(stepName) && !missedSet.has(stepName)) addAction(s, 'pending');
      });
      missedSteps.forEach((s) => {
        const remark = undefined;
        const isMandatory = (violations ?? []).some(
          (v) => v.toLowerCase().includes('mandatory') && v.toLowerCase().includes(s.step ?? '')
        );
        addAction(
          s,
          isMandatory ? 'missed_mandatory' : 'missed_inorder',
          remark ?? (isMandatory ? 'Mandatory missed' : 'In order missed')
        );
      });
    }
    sections.push({ state, actions: sectionActions });
  });

  // Flat list of all actions (for scroll index and counts) — same order as rendered (state1 actions, state2 actions, ...)
  const actions = sections.flatMap((s) => s.actions);
  return { sections, actions };
}

function buildEntityList(
  card: AgentAssistCardData | null,
  pendingEntities: Array<{ step?: string; entities: string[] }>
): LiveAssistEntityItem[] {
  if (!card) return [];
  const full = card.state_entities_full;
  if (full && full.length > 0) {
    const stage = (item: { stage?: string | null; status?: string | null; filled?: boolean }) =>
      (item.stage ?? '').toLowerCase();
    const statusMap = (item: {
      status?: string | null;
      stage?: string | null;
      filled?: boolean;
      value?: string | null;
    }): LiveAssistEntityStatus => {
      const s = (item.status ?? '').toLowerCase();
      const st = stage(item);
      const hasValue = item.filled ?? (typeof item.value === 'string' && (item.value ?? '').trim().length > 0);
      if (s === 'completed') return st === 'ack' ? 'completed_ack' : 'completed';
      if (hasValue && s !== 'completed') {
        if (st === 'confirm') return 'to_confirm';
        return 'incomplete_other';
      }
      if (s === 'to_repeat') return 'to_repeat';
      return 'not_filled';
    };
    return full.map((item) => ({
      entity: item.entity_name ?? '',
      value: serializeEntityValue(item.value),
      filled: item.filled ?? serializeEntityValue(item.value) != null,
      status: statusMap(item),
    }));
  }
  const collected = card.collected_entities ?? [];
  const recent = card.recent_collected_entities ?? [];
  const pendingSet = new Set(
    (pendingEntities ?? []).flatMap((p) => p.entities ?? [])
  );
  const byEntity = new Map<string, LiveAssistEntityItem>();
  const order: string[] = [];

  const add = (
    entity: TurnAnalysisEntityRecord,
    status: LiveAssistEntityItem['status']
  ) => {
    const name = entity.entity;
    if (!name) return;
    if (!byEntity.has(name)) {
      order.push(name);
      byEntity.set(name, {
        ...entity,
        status,
      });
    } else {
      const existing = byEntity.get(name)!;
      if (status === 'completed' || (status === 'to_confirm' && existing.status === 'not_filled'))
        existing.status = status;
    }
  };

  [...recent, ...collected].forEach((e) => {
    const filled = e.filled ?? (typeof e.value === 'string' && (e.value ?? '').trim().length > 0);
    add(e, filled ? 'completed' : 'to_confirm');
  });
  pendingSet.forEach((name) => {
    if (!byEntity.has(name))
      add({ entity: name, value: null, filled: false }, 'not_filled');
  });

  return order.map((name) => byEntity.get(name)!).filter(Boolean);
}

const LiveAssistCard: React.FC<LiveAssistCardProps> = ({
  allTurns,
  currentTurnNumber,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const entityContainerRef = useRef<HTMLDivElement>(null);
  const lastScrollTurnRef = useRef<number>(0);

  const { sections, actions } = useMemo(
    () => buildTimeline(allTurns, currentTurnNumber),
    [allTurns, currentTurnNumber]
  );

  const currentTurn =
    allTurns.find((t) => t.turn_number === currentTurnNumber) ??
    (allTurns.length > 0 ? allTurns[allTurns.length - 1] : null);
  const card = currentTurn?.agent_card ?? null;
  const entities = useMemo(
    () => buildEntityList(card, card?.pending_entities ?? []),
    [card]
  );

  // Entity "updated this turn" set for list order + flash (diff current vs previous turn).
  const guidanceForTurn = useMemo(() => {
    if (!currentTurn || !card) return { entityNames: [] as string[] };

    const prevTurn = allTurns.find((t) => t.turn_number === currentTurnNumber - 1) ?? null;
    const prevCard = prevTurn?.agent_card ?? null;
    const completedStatus = (s: string | null | undefined) => (s ?? '').toLowerCase() === 'completed';
    const isFilled = (e: StateEntityFullItem) =>
      completedStatus(e.status) || e.filled === true;
    const entityKey = (e: StateEntityFullItem) => String(e.entity_name ?? '').trim();
    const currFilled = (card.state_entities_full ?? []).filter(isFilled).map(entityKey).filter(Boolean);
    const prevFilled = (prevCard?.state_entities_full ?? []).filter(isFilled).map(entityKey).filter(Boolean);
    const prevEntitySet = new Set(prevFilled);
    const entityNames = Array.from(new Set(currFilled)).filter((name: string) => !prevEntitySet.has(name));
    return { entityNames };
  }, [allTurns, currentTurnNumber, currentTurn, card]);

  // Guidance panel: DB guidance for this turn, excluding action-completed and entity-collected (shown in timeline/entity list).
  const otherGuidanceLines = useMemo(() => {
    const raw = currentTurn?.guidance ?? [];
    return raw.filter((line) => {
      const s = (line ?? '').trim();
      if (!s) return false;
      if (s.startsWith('[OK] ')) return false;
      if (/successfully collected|^collected:?\s/i.test(s)) return false;
      return true;
    });
  }, [currentTurn?.guidance]);

  // Entity list order: updated this turn at top (same order as in list), then rest alphabetically. Flash class from updated set.
  const updatedEntitySet = useMemo(
    () => new Set(guidanceForTurn.entityNames),
    [guidanceForTurn.entityNames]
  );
  const sortedEntities = useMemo(() => {
    const updated = entities.filter((e) => updatedEntitySet.has(e.entity));
    const rest = entities
      .filter((e) => !updatedEntitySet.has(e.entity))
      .sort((a, b) => a.entity.localeCompare(b.entity, undefined, { sensitivity: 'base' }));
    return [...updated, ...rest];
  }, [entities, updatedEntitySet]);

  const counts = useMemo(() => {
    const completed = actions.filter((a) => a.status.startsWith('completed')).length;
    const pending = actions.filter((a) => a.status === 'pending' || a.status === 'next').length;
    const missed = actions.filter((a) => a.status.startsWith('missed')).length;
    // Violations pile: actions with at least one guard violation (sensitivity, etc.), not mandatory/order-missed
    const violations = actions.filter((a) => {
      const list = a.violations ?? [];
      return list.some((v) => !isMissedTypeViolation(v));
    }).length;
    return { completed, pending, missed, violations };
  }, [actions]);

  // Compute index range for the current state's actions (last section)
  const currentStateRange = useMemo(() => {
    if (sections.length === 0) return { start: 0, end: 0 };
    const lastIdx = sections.length - 1;
    const start = sections.slice(0, lastIdx).reduce((sum, s) => sum + s.actions.length, 0);
    const end = start + sections[lastIdx].actions.length;
    return { start, end };
  }, [sections]);

  const liveIndex = useMemo(() => {
    const { start, end } = currentStateRange;
    for (let i = start; i < end; i++) {
      if (actions[i]?.status === 'live') return i;
    }
    return -1;
  }, [actions, currentStateRange]);

  const nextIndex = useMemo(() => {
    const { start, end } = currentStateRange;
    for (let i = start; i < end; i++) {
      const st = actions[i]?.status;
      // Treat both explicit 'next' and the first pending action as "next" target
      if (st === 'next' || st === 'pending') return i;
    }
    return -1;
  }, [actions, currentStateRange]);
  const scrollAnchorIndex = liveIndex >= 0 ? liveIndex : (nextIndex >= 0 ? nextIndex : -1);
  const lastCompletedIndex = useMemo(() => {
    let i = -1;
    actions.forEach((a, idx) => {
      if (a.status.startsWith('completed')) i = idx;
    });
    return i;
  }, [actions]);

  useEffect(() => {
    if (currentTurnNumber === lastScrollTurnRef.current && scrollAnchorIndex < 0) return;
    lastScrollTurnRef.current = currentTurnNumber;
    // Scroll actions pane to the specific action index if available
    if (scrollAnchorIndex >= 0 && containerRef.current) {
      const item = containerRef.current.querySelector<HTMLElement>(
        `[data-timeline-index="${scrollAnchorIndex}"]`
      );
      if (item) {
        requestAnimationFrame(() => {
          item.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        });
      }
    } else if (containerRef.current) {
      // Fallback: scroll container into view
      requestAnimationFrame(() => {
        containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    }
    // Parallel scroll entities pane to entity matching primaryEntityName of live/next action
    if (scrollAnchorIndex >= 0 && entityContainerRef.current) {
      const targetAction = actions[scrollAnchorIndex];
      const primary = targetAction?.primaryEntityName;
      if (primary) {
        const entityEl = entityContainerRef.current.querySelector<HTMLElement>(
          `[data-entity-name="${primary}"]`
        );
        if (entityEl) {
          requestAnimationFrame(() => {
            entityEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          });
        }
      }
    }
  }, [currentTurnNumber, scrollAnchorIndex, lastCompletedIndex, actions]);

  /** Last matching index = most recent occurrence in timeline order (later states / later steps). */
  const scrollToCategory = (category: 'missed' | 'pending' | 'completed' | 'violations') => {
    let idx = -1;
    actions.forEach((a, i) => {
      if (category === 'violations') {
        const list = a.violations ?? [];
        if (list.some((v) => !isMissedTypeViolation(v))) idx = i;
      } else if (category === 'missed') {
        if (a.status.startsWith('missed')) idx = i;
      } else if (category === 'pending') {
        if (a.status === 'pending' || a.status === 'next') idx = i;
      } else if (a.status.startsWith('completed')) {
        idx = i;
      }
    });
    if (idx >= 0 && containerRef.current) {
      const item = containerRef.current.querySelector(`[data-timeline-index="${idx}"]`);
      item?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  if (allTurns.length === 0) {
    return (
      <div className="live-assist-card">
        <div className="live-assist-card-piles">
          <button type="button" className="live-assist-pile pile-completed" disabled>
            Completed 0
          </button>
          <button type="button" className="live-assist-pile pile-pending" disabled>
            Pending 0
          </button>
          <button type="button" className="live-assist-pile pile-missed" disabled>
            Missed 0
          </button>
          <button type="button" className="live-assist-pile pile-violations" disabled>
            Violations 0
          </button>
        </div>
        <div className="live-assist-card-timeline" ref={containerRef}>
          <p className="live-assist-card-empty">No turns yet. Play to see timeline.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="live-assist-card">
      <div className="live-assist-card-piles">
        <button
          type="button"
          className="live-assist-pile pile-completed"
          onClick={() => scrollToCategory('completed')}
        >
          Completed {counts.completed}
        </button>
        <button
          type="button"
          className="live-assist-pile pile-pending"
          onClick={() => scrollToCategory('pending')}
        >
          Pending {counts.pending}
        </button>
        <button
          type="button"
          className="live-assist-pile pile-missed"
          onClick={() => scrollToCategory('missed')}
        >
          Missed {counts.missed}
        </button>
        <button
          type="button"
          className="live-assist-pile pile-violations"
          onClick={() => scrollToCategory('violations')}
        >
          Violations {counts.violations}
        </button>
      </div>

      <div className="live-assist-card-main">
        <div className="live-assist-card-timeline" ref={containerRef}>
          {(() => {
            let flatIndex = 0;
            return sections.map((sec) => (
              <div key={sec.state.key} className="live-assist-timeline-section">
                <div className="live-assist-timeline-state-marker">
                  {sec.state.stateTitle}
                </div>
                {sec.actions.map((a) => {
                  const index = flatIndex++;
                  const violations = a.violations ?? [];
                  const constraintViolations = violations.filter(isMissedTypeViolation);
                  const otherViolations = violations.filter((v) => !isMissedTypeViolation(v));
                  const scriptKeys = a.scriptConstraints ?? [];
                  const subflowLbl = subflowCornerLabelFromName(a.subflowName);
                  const showMetaChips = scriptKeys.length > 0 || constraintViolations.length > 0;
                  return (
                    <div
                      key={a.key}
                      data-timeline-index={index}
                      className={`live-assist-timeline-action aal-timeline-step status-${a.status}${
                        subflowLbl ? ' aal-timeline-step--has-subflow' : ''
                      }`}
                    >
                      {subflowLbl && (
                        <div className="aal-step-subflow-badge" title={subflowLbl}>
                          {subflowLbl}
                        </div>
                      )}
                      <span className="live-assist-action-indicator">
                        {a.status.startsWith('skipped_') ? (
                          <span className="material-icons live-assist-skipped-warn-icon" aria-hidden>
                            warning
                          </span>
                        ) : a.status.startsWith('completed') ? (
                          '✓'
                        ) : a.status === 'live' ? (
                          '●'
                        ) : a.status === 'next' ? (
                          '◎'
                        ) : a.status.startsWith('missed') ? (
                          '✗'
                        ) : (
                          '○'
                        )}
                      </span>
                      <div className="live-assist-action-body">
                        <div className="live-assist-action-title-row">
                          <span className="live-assist-action-step">
                            {a.displayTitle ?? formatStepLabel(a.step)}
                          </span>
                          {showMetaChips && (
                            <div className="aal-step-meta-chips" aria-label="Script constraints">
                              {scriptKeys.map((key) => {
                                const { text, title } = scriptConstraintChipMeta(key);
                                return (
                                  <span key={key} className="aal-step-chip" title={title}>
                                    {text}
                                  </span>
                                );
                              })}
                              {constraintViolations.length > 0 && (
                                <span
                                  className="aal-step-chip aal-step-chip--warn"
                                  title={constraintViolations.map((v) => v.message).join(' · ')}
                                >
                                  !
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        {a.remark && (
                          <span className="live-assist-action-remark">{a.remark}</span>
                        )}
                        {otherViolations.length > 0 && (
                          <div className="live-assist-action-violations">
                            {otherViolations.map((v, vi) => (
                              <div key={vi} className="live-assist-violation-line" title={v.message}>
                                <span className="live-assist-violation-icon" aria-hidden>
                                  !
                                </span>
                                <span className="live-assist-violation-text">
                                  {v.violationType.replace(/_/g, ' ')}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ));
          })()}
        </div>

        {sortedEntities.length > 0 && (
          <div className="live-assist-card-entities" ref={entityContainerRef}>
            <span className="live-assist-card-section-title">Entities</span>
            {sortedEntities.map((e, idx) => (
              <div
                key={`${e.entity}-${idx}`}
                data-entity-name={e.entity}
                className={`live-assist-entity-row entity-${e.status}${updatedEntitySet.has(e.entity) ? ' live-assist-entity-row-updated' : ''}`}
              >
                <span className="live-assist-entity-label">
                  {formatStepLabel(e.entity)}
                </span>
                {e.status === 'not_filled' ? (
                  <span className="live-assist-entity-value">· · ·</span>
                ) : (
                  <EntityValueCell value={e.value} valueClassName="live-assist-entity-value" />
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Single-line guidance (DB guidance for this turn, excluding completed/collected); highlighted when present */}
      <div
        className={`live-assist-card-guidance ${otherGuidanceLines.length > 0 ? 'live-assist-card-guidance-has-text' : ''}`}
      >
        <div className="live-assist-card-guidance-body">
          {otherGuidanceLines.length === 0 ? (
            <span className="live-assist-guidance-empty">No guidance for this turn.</span>
          ) : (
            <span
              className="live-assist-guidance-oneline"
              title={otherGuidanceLines.join(' · ')}
            >
              {otherGuidanceLines.join(' · ')}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default LiveAssistCard;
